import { useEffect, useState, useContext } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import * as XLSX from "xlsx";
import { getSources } from "../../api/sourceApi";
import { AuthContext } from "../../Context/AuthContext";

export default function SourceForm({ visible, onHide, onSubmit, initialData, products = [], uploadMode = false }) {
  const [form, setForm] = useState({ key_id: "", product_ids: [] });
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { auth } = useContext(AuthContext);
  const token = auth?.token;

  useEffect(() => {
    if (!uploadMode) {
      if (initialData) {
        setForm({
          key_id: initialData.key_id || "",
          product_ids: Array.isArray(initialData.Products)
            ? initialData.Products.map(p => p.id)
            : []
        });
      } else {
        setForm({ key_id: "", product_ids: [] });
      }
    } else {
      setPreview([]);
      setErrors([]);
    }
  }, [initialData, uploadMode, visible]);

  const handleFile = async (event) => {
    const file = event.files ? event.files[0] : event.target.files[0];
    if (!file) return;

    let data = [];
    try {
      if (file.name.endsWith(".json")) {
        const text = await file.text();
        data = JSON.parse(text);
      } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer);
        const ws = wb.Sheets[wb.SheetNames[0]];
        data = XLSX.utils.sheet_to_json(ws);
      }
    } catch {
      alert("Error reading file. Please check the file format.");
      return;
    }

    if (!Array.isArray(data)) data = [];

    const uniqueMap = {};
    const uniqueData = [];
    const newErrors = [];

    data.forEach((d, i) => {
      const key_id = (d.key_id || "").toLowerCase().trim();
      let product_ids = [];

      if (!key_id) {
        newErrors.push(`Row ${i + 1}: key_id is required`);
        return;
      }

      if (Array.isArray(d.product_ids)) {
        product_ids = d.product_ids.map(p => {
          if (typeof p === "number") return p;
          const match = products.find(prod => prod.name.toLowerCase() === String(p).toLowerCase());
          return match ? match.id : null;
        }).filter(Boolean);
      } else if (typeof d.products === "string") {
        product_ids = d.products.split(",").map(pName => {
          const match = products.find(prod => prod.name.toLowerCase() === pName.trim().toLowerCase());
          return match ? match.id : null;
        }).filter(Boolean);
      }

      if (uniqueMap[key_id]) {
        newErrors.push(`Row ${i + 1}: Duplicate key_id: ${key_id}`);
        return;
      }

      uniqueMap[key_id] = true;
      uniqueData.push({ key_id, product_ids });
    });

    setPreview(uniqueData);
    setErrors(newErrors);
  };

  const handlePreviewChange = (index, field, value) => {
    const newPreview = [...preview];
    if (field === "key_id") newPreview[index].key_id = value.toLowerCase();
    else if (field === "product_ids") newPreview[index].product_ids = value;

    const seen = {};
    const newErrors = [];
    newPreview.forEach((d, i) => {
      const key = d.key_id.trim().toLowerCase();
      if (!key) newErrors.push(`Row ${i + 1}: key_id is required`);
      else if (seen[key]) newErrors.push(`Duplicate key_id: ${d.key_id}`);
      seen[key] = true;
    });
    setErrors(newErrors);
    setPreview(newPreview);
  };

  const handleDeleteRow = index => {
    const newPreview = [...preview];
    newPreview.splice(index, 1);
    setPreview(newPreview);

    const seen = {};
    const newErrors = [];
    newPreview.forEach((d, i) => {
      const key = d.key_id.trim().toLowerCase();
      if (!key) newErrors.push(`Row ${i + 1}: key_id is required`);
      else if (seen[key]) newErrors.push(`Duplicate key_id: ${d.key_id}`);
      seen[key] = true;
    });
    setErrors(newErrors);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (uploadMode) {
        if (!preview.length) {
          alert("No valid data to upload");
          return;
        }
        if (errors.length) {
          alert("Please fix the following errors before upload:\n" + errors.join("\n"));
          return;
        }
        await onSubmit(preview);
        return;
      }

      if (!form.key_id) {
        alert("Source Key is required");
        return;
      }

      await onSubmit({
        key_id: form.key_id.toLowerCase(),
        product_ids: form.product_ids || []
      });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      header={uploadMode ? "Upload Sources" : initialData ? "Edit Source" : "Add Source"}
      visible={visible}
      onHide={onHide}
      modal
      className="w-6"
    >
      <div className="p-3 flex flex-column gap-3" onKeyDown={handleKeyDown}>
        {!uploadMode && (
          <>
            <div className="flex flex-column gap-1">
              <label>Source Key</label>
              <InputText
                value={form.key_id}
                onChange={(e) => setForm({ ...form, key_id: e.target.value })}
              />
            </div>
            <div className="flex flex-column gap-1">
              <label>Products (Optional)</label>
              <MultiSelect
                value={form.product_ids}
                options={products.length > 0
                  ? products.map(p => ({ label: p.name, value: p.id }))
                  : [{ label: "No products available", value: null }]}
                onChange={e => setForm({ ...form, product_ids: e.value })}
                display="chip"
                placeholder={products.length > 0 ? "Select products" : "No products available"}
                disabled={products.length === 0}
              />
            </div>
          </>
        )}

        {uploadMode && (
          <>
            <div className="flex justify-content-center">
              <FileUpload
                mode="basic"
                accept=".json,.xlsx,.xls"
                chooseLabel="Upload"
                onSelect={handleFile}
                auto
                chooseOptions={{
              icon: "pi pi-upload",
              style: {
                backgroundColor: "#d87347ff",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "0.5rem 1rem",
                cursor: "pointer",
              },
            }}
            style={{
              border: "2px dashed #bebbbaff",
              borderRadius: 8,
              padding: 50,
              background: "#f8f9fa",
            }}
              />
            </div>

            {errors.length > 0 && (
              <div className="p-3 w-full" style={{
                maxHeight: 120,
                overflowY: "auto",
                backgroundColor: '#ffe6e6',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                color: '#721c24'
              }}>
                <b>Errors:</b>
                <ul className="m-0 pl-3">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            {preview.length > 0 && (
              <div style={{ maxHeight: 300, overflow: "auto" }}>
                <table className="p-datatable-table">
                  <thead>
                    <tr>
                      <th>Key ID</th>
                      <th>Products (Optional)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((d, i) => (
                      <tr key={i}>
                        <td>
                          <InputText
                            value={d.key_id}
                            onChange={e => handlePreviewChange(i, "key_id", e.target.value)}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>
                          <MultiSelect
                            value={d.product_ids}
                            options={products.length > 0
                              ? products.map(p => ({ label: p.name, value: p.id }))
                              : [{ label: "No products available", value: null }]}
                            onChange={e => handlePreviewChange(i, "product_ids", e.value)}
                            display="chip"
                            placeholder={products.length > 0 ? "Select products" : "No products available"}
                            disabled={products.length === 0}
                          />
                        </td>
                        <td>
                          <Button icon="pi pi-trash" className="p-button-danger p-button-text" onClick={() => handleDeleteRow(i)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <Button
          label={isLoading ? (uploadMode ? "Uploading..." : "Submitting...") : (uploadMode ? "Upload" : "Submit")}
          icon={isLoading ? "pi pi-spin pi-spinner" : ""}
          onClick={handleSubmit}
          disabled={isLoading}
          className=" p-button-block custom-splitbutton"
          style={{ backgroundColor: "#FC5F1CFF", color: "#fff" }}
        />
      </div>
    </Dialog>
  );
}
